import React, { useState, useEffect, useCallback, useMemo } from "react";
import apiClient from "../../../services/apiClient";
import { useUserData } from "../../../hooks/useUserData";
import { useLoader } from "../../../context/LoaderContext";
import ReportTable from "../../common/ReportTable/ReportTable";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import DateRangeFilter from "../../common/DateRangeFilter";
import Dropdown from "../../common/dropDown";
import utils from "../../../utils/utils";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import {
  parsePermissions,
  hasPermission,
  getAllPermissionKeys,
} from "../../../utils/permissionUtils";
import { matchTextColumnSearch } from "./isfReportTable";
import "./isfReportTable.scss";

const AUDIT_LOG_FIELD_OPTIONS = [
  { key: "StudyName", label: "Study" },
  { key: "FileName", label: "File" },
  { key: "Category", label: "Category" },
  { key: "CreatedBy", label: "Created By" },
  { key: "CreatedOn", label: "Created On" },
];

const DEFAULT_AUDIT_LOG_COLUMNS = [
  "StudyName",
  "FileName",
  "Category",
  "CreatedBy",
  "CreatedOn",
];

function getAuditLogCategory(row) {
  const name = String(row?.FileName || "").toUpperCase();
  if (name.includes("FISF") || name.includes("EPSF") || name.includes("_PSF")) {
    return "ePSF";
  }
  if (name.includes("EISF")) return "eISF";
  return "—";
}

function getAuditLogDownloadUrl(row) {
  return row?.DownloadUrl ?? row?.FileUrl ?? row?.AzureUrl ?? row?.Url ?? "";
}

const AUDIT_EMPTY_DATE_RANGE = {
  preset: "All Time",
  startDate: "",
  endDate: "",
};

const AUDIT_DATE_PRESETS = [
  "Last 30 Days",
  "Last 90 Days",
  "This Year",
  "All Time",
  "Custom",
];

const startOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);

const endOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

const matchesAuditDateRange = (value, range) => {
  if (
    !range ||
    range.preset === "All Time" ||
    (!range.startDate && !range.endDate)
  ) {
    return true;
  }
  const raw = String(value ?? "").trim();
  if (!raw || raw.toUpperCase() === "N/A") return false;
  const date = utils.parseDateInput(raw);
  if (!date) return false;
  const start = range.startDate
    ? startOfDay(utils.parseDateInput(range.startDate))
    : null;
  const end = range.endDate
    ? endOfDay(utils.parseDateInput(range.endDate))
    : null;
  if (start && date < start) return false;
  if (end && date > end) return false;
  return true;
};

const SAMPLE_AUDIT_LOGS = [
  // {
  //   StudyName: "Study Alpha",
  //   FileName: "audit-log-alpha-2025-02.pdf",
  //   CreatedBy: "John Smith",
  //   CreatedOn: "2025-02-20 10:30",
  //   DownloadUrl: "https://example.com/files/audit-alpha.pdf",
  // },
  // {
  //   StudyName: "Study Beta",
  //   FileName: "audit-log-beta-2025-02.xlsx",
  //   CreatedBy: "Jane Doe",
  //   CreatedOn: "2025-02-21 14:15",
  //   DownloadUrl: "https://example.com/files/audit-beta.xlsx",
  // },
  // {
  //   StudyName: "Study Gamma",
  //   FileName: "audit-log-gamma-2025-02.pdf",
  //   CreatedBy: "John Smith",
  //   CreatedOn: "2025-02-22 09:00",
  //   DownloadUrl: "https://example.com/files/audit-gamma.pdf",
  // },
];

const DownloadAuditLog = () => {
  const { userData } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();

  const loggedInRole = String(userData?.Role || "").toLowerCase();
  const isSystemAdmin = loggedInRole === "system administrator";

  const rawLoggedInPerms =
    userData?.Permission ||
    userData?.UserPermission ||
    userData?.Permissions ||
    userData?.PermissionString ||
    "";

  const userPermissions = isSystemAdmin
    ? getAllPermissionKeys()
    : parsePermissions(rawLoggedInPerms);

  const canAuditLog =
    hasPermission(userPermissions, "AUDIT_LOG") ||
    hasPermission(userPermissions, "DOWNLOAD_AUDIT_LOG") ||
    hasPermission(userPermissions, "REQUEST_AUDIT_LOG");
  const canDownloadAuditLog = canAuditLog;
  const canRequestAuditLog = canAuditLog;

  const [auditLogs, setAuditLogs] = useState(SAMPLE_AUDIT_LOGS);
  const [createdDateRange, setCreatedDateRange] = useState({
    ...AUDIT_EMPTY_DATE_RANGE,
  });
  const [listLoading, setListLoading] = useState(true);
  const [studies, setStudies] = useState([]);
  const [studyOptions, setStudyOptions] = useState([]);
  const [templateCategoryOptions, setTemplateCategoryOptions] = useState([
    { label: "eISF", value: "eISF" },
    { label: "ePSF", value: "ePSF" },
  ]);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestForm, setRequestForm] = useState({
    study: null,
    templateCategory: null,
  });

  useEffect(() => {
    if (userData) {
      if (!canDownloadAuditLog && !canRequestAuditLog) {
        showAlert({
          title: "Access Denied",
          message: "You don't have permission to download or request audit logs.",
          type: "danger",
        });
        setAuditLogs([]);
        setStudies([]);
        setStudyOptions([]);
        setShowRequestModal(false);
        return;
      }

      if (canDownloadAuditLog) getAuditLogList();
      if (canRequestAuditLog) getStudyList();
    }
  }, [userData, canDownloadAuditLog, canRequestAuditLog]);

  const getStudyList = async () => {
    try {
      const response = await apiClient.post(API_ENDPOINTS.ISF.GETSTUDYLIST, {
        OrganizationId: userData.OrganizationId,
        Action: "GetEISfStudies",
        CreatedBy: userData.StaffId,
      });
      setStudies(response.data || []);
      setStudyOptions(
        utils.mapToOptions(response.data || [], "StudyName", "StudyId"),
      );
    } catch (error) {
      console.error("Error fetching studies:", error);
    }
  };

  const getAuditLogList = async () => {
    if (!userData?.OrganizationId) return;
    try {
      setListLoading(true);
      const response = await apiClient.post(
        API_ENDPOINTS.ISF.GET_DOWNLOAD_AUDIT_LOGS,
        {
          OrganizationIdRef: userData.OrganizationId,
          CreatedBy: userData.StaffId,
          ActionType: "GetExportedLinkData",
        },
      );
      const list = response?.data?.Table ?? response?.data ?? [];
      const rows = Array.isArray(list) ? list : [];
      setAuditLogs(rows.length > 0 ? rows : SAMPLE_AUDIT_LOGS);
    } catch (error) {
      console.error("Error fetching audit logs:", error);
      setAuditLogs(SAMPLE_AUDIT_LOGS);
    } finally {
      setListLoading(false);
    }
  };

  const handleRefresh = () => {
    if (!canDownloadAuditLog) {
      showAlert({
        title: "Permission Denied",
        message: "You don't have permission to download audit logs.",
        type: "warning",
      });
      return;
    }
    getAuditLogList();
  };

  const handleRequestAuditLog = () => {
    if (!canRequestAuditLog) {
      showAlert({
        title: "Permission Denied",
        message: "You don't have permission to request a new audit log.",
        type: "warning",
      });
      return;
    }
    setRequestForm({ study: null, templateCategory: null });
    setShowRequestModal(true);

    // If the dropdown data isn't loaded yet, load it when opening the modal.
    if (!studyOptions?.length && canRequestAuditLog) getStudyList();
  };

  const handleStudySelect = (selectedStudy) => {
    setRequestForm((prev) => ({
      ...prev,
      study: selectedStudy || null,
      templateCategory: null,
    }));

    if (!selectedStudy?.value) {
      setTemplateCategoryOptions([
        { label: "eISF", value: "eISF" },
        { label: "ePSF", value: "ePSF" },
      ]);
      return;
    }

    const studyConfig = studies.find(
      (s) =>
        String(s.StudyId).toLowerCase() ===
        String(selectedStudy.value).toLowerCase(),
    );
    if (!studyConfig) {
      setTemplateCategoryOptions([
        { label: "eISF", value: "eISF" },
        { label: "ePSF", value: "ePSF" },
      ]);
      return;
    }

    const options = [];
    if (studyConfig.HaseISFTemplate)
      options.push({ label: "eISF", value: "eISF" });
    if (studyConfig.HasePSFTemplate)
      options.push({ label: "ePSF", value: "ePSF" });
    setTemplateCategoryOptions(
      options.length
        ? options
        : [
            { label: "eISF", value: "eISF" },
            { label: "ePSF", value: "ePSF" },
          ],
    );
  };

  const handleTemplateCategorySelect = (selected) => {
    setRequestForm((prev) => ({ ...prev, templateCategory: selected || null }));
  };

  const submitRequest = async () => {
    if (!canRequestAuditLog) {
      showAlert({
        title: "Permission Denied",
        message: "You don't have permission to request a new audit log.",
        type: "warning",
      });
      return;
    }
    if (!requestForm.study?.value) {
      showAlert({
        title: "Validation",
        message: "Please select a study.",
        type: "warning",
      });
      return;
    }
    if (!requestForm.templateCategory?.value) {
      showAlert({
        title: "Validation",
        message: "Please select a template category.",
        type: "warning",
      });
      return;
    }
    try {
      showLoader("Requesting audit log...");
      await apiClient.post(API_ENDPOINTS.ISF.REQUEST_DOWNLOAD_AUDIT_LOG, {
        OrganizationIdRef: userData.OrganizationId,
        CreatedBy: userData.StaffId,
        StudyIdRef: requestForm.study.value,
        ActionType: "ExportData",
        StudyName: requestForm.study.label,
      });
      showAlert({
        title: "Success",
        message:
          "Downloading is in Progress. Once download process completed. File will display in the list",
        type: "success",
      });
      setShowRequestModal(false);
      getAuditLogList();
    } catch (error) {
      console.error("Error requesting audit log:", error);
      showAlert({
        title: "Error",
        message:
          error?.response?.data?.message ||
          "Failed to submit audit log request.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const handleDownload = useCallback(
    (row) => {
      if (!canDownloadAuditLog) {
        showAlert({
          title: "Permission Denied",
          message: "You don't have permission to download audit logs.",
          type: "warning",
        });
        return;
      }
      const url = getAuditLogDownloadUrl(row);
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        showAlert({
          title: "Download",
          message: "No download link available for this file.",
          type: "warning",
        });
      }
    },
    [canDownloadAuditLog, showAlert],
  );

  const renderReportCell = useCallback(
    (row, field) => {
      if (field === "FileName") {
        const fileName = row.FileName || "—";
        if (!canDownloadAuditLog || !getAuditLogDownloadUrl(row)) {
          return fileName;
        }
        return (
          <button
            type="button"
            className="report-table__cell-link"
            title="Download file"
            onClick={() => handleDownload(row)}
          >
            {fileName}
          </button>
        );
      }
      if (field === "Category") {
        return getAuditLogCategory(row);
      }
      if (field === "CreatedOn") {
        return utils.formatDateTimeDisplay(row.CreatedOn) || row.CreatedOn || "—";
      }
      return String(row[field] ?? "—");
    },
    [canDownloadAuditLog, handleDownload],
  );

  const filteredAuditLogs = useMemo(
    () =>
      (auditLogs || []).filter((row) =>
        matchesAuditDateRange(row?.CreatedOn, createdDateRange),
      ),
    [auditLogs, createdDateRange],
  );

  const renderColumnSearch = useCallback((field, options) => {
    if (field !== "Category" && field !== "CreatedOn") return undefined;
    if (options?.checkOnly) return true;
    if (field === "CreatedOn") {
      return (
        <DateRangeFilter
          value={createdDateRange}
          onChange={(range) =>
            setCreatedDateRange(range || { ...AUDIT_EMPTY_DATE_RANGE })
          }
          placeholder="Created on range"
          presetOptions={AUDIT_DATE_PRESETS}
          compact
          disabled={options?.disabled}
        />
      );
    }
    return (
      <select
        className="report-table__column-search"
        value={options?.value || ""}
        disabled={options?.disabled}
        aria-label="Filter by category"
        onChange={(event) => options?.onChange?.(event.target.value)}
      >
        <option value="">All</option>
        <option value="eisf">eISF</option>
        <option value="epsf">ePSF</option>
      </select>
    );
  }, [createdDateRange]);

  const matchColumnSearch = useCallback((row, field, searchValue) => {
    const term = String(searchValue || "").trim().toLowerCase();
    if (!term) return true;
    if (field === "CreatedOn") return true;
    if (field === "Category") {
      return getAuditLogCategory(row).toLowerCase() === term;
    }
    return matchTextColumnSearch(row, field, searchValue, [
      "CreatedOn",
      "Category",
    ]);
  }, []);

  const getFieldLabel = useCallback(
    (field) =>
      AUDIT_LOG_FIELD_OPTIONS.find((option) => option.key === field)?.label ||
      field,
    [],
  );

  const layoutStorageKey = userData?.OrganizationId
    ? `isf-download-audit-log-report-v2-${userData.OrganizationId}`
    : "";

  return (
    <div className="isf-report-page">
      <PageBreadcrumb
        items={[
          { label: "Home", path: "/notification-center" },
          { label: "Audit Log" },
        ]}
        actions={[
          ...(canDownloadAuditLog
            ? [
                {
                  icon: "fas fa-sync-alt",
                  label: "Refresh Data",
                  onClick: handleRefresh,
                },
              ]
            : []),
          ...(canRequestAuditLog
            ? [
                {
                  icon: "fas fa-plus",
                  label: "Request Audit Log",
                  onClick: handleRequestAuditLog,
                },
              ]
            : []),
        ]}
      />

      <div className="isf-report-table-wrap">
        <ReportTable
          rows={filteredAuditLogs}
          fieldOptions={AUDIT_LOG_FIELD_OPTIONS}
          defaultColumns={DEFAULT_AUDIT_LOG_COLUMNS}
          getFieldLabel={getFieldLabel}
          renderCell={renderReportCell}
          renderColumnSearch={renderColumnSearch}
          matchColumnSearch={matchColumnSearch}
          pageSizeOptions={[10, 25, 50, 100]}
          columnSearchMode="client"
          persistLayoutKey={layoutStorageKey}
          loading={listLoading}
        />
      </div>

      {showRequestModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Request Audit Log</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowRequestModal(false)}
                />
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-12">
                    <label className="form-label">Study</label>
                    <Dropdown
                      options={studyOptions}
                      selected={requestForm.study}
                      onSelect={handleStudySelect}
                      isMulti={false}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-12">
                    <label className="form-label">Template Category</label>
                    <Dropdown
                      options={templateCategoryOptions}
                      selected={requestForm.templateCategory}
                      onSelect={handleTemplateCategorySelect}
                      isMulti={false}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowRequestModal(false)}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={submitRequest}
                  disabled={!canRequestAuditLog}
                >
                  Submit Request
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <InfoAlert
        isOpen={alertState.isOpen}
        onClose={hideAlert}
        {...alertState}
      />
    </div>
  );
};

export default DownloadAuditLog;
